/* Copyright (c) Trimble Inc. All rights reserved. */

/**
 * Authentication Library Constants
 */

/**
 * JWT Token expiration buffer time in seconds
 * Tokens will be considered expired this many seconds before their actual expiration time
 * This prevents race conditions where a token expires between validation and use
 */
export const JWT_TOKEN_EXPIRATION_BUFFER_SECONDS = 60; // 1 minute buffer

/**
 * Session storage key for PKCE code verifier
 */
export const PKCE_CODE_VERIFIER_STORAGE_KEY = 'pkce_code_verifier';

/**
 * Session storage key for redirect path
 */
export const AUTH_REDIRECT_PATH_STORAGE_KEY = 'auth_redirect_path';

/**
 * Master switch for the proactive session-expiry monitor / dialog.
 *
 * When `false` (default) the UI never logs out or redirects to login on its own
 * just because the access token's `exp` has passed. Re-authentication is driven
 * by the server instead: the next API call that comes back `401` (for example
 * "View secret" -> GET /secrets/{secretId}) triggers the login redirect.
 *
 * Set to `true` to restore the "Session expiring soon" dialog.
 */
export const SESSION_EXPIRY_MONITOR_ENABLED = false;

/**
 * Show the session expiry dialog this many seconds before the token expires.
 * Default: 120 seconds (2 minutes before expiry).
 */
export const SESSION_EXPIRY_WARN_BEFORE_SECONDS = 60 * 2; // 2 minutes;

/**
 * Duration of the countdown timer in the session expiry dialog (in seconds).
 * After this countdown the user is automatically logged out.
 * Default: 60 seconds.
 */
export const SESSION_EXPIRY_COUNTDOWN_SECONDS = 60;

/**
 * OAuth callback query parameters that must be stripped from URLs after
 * a successful (or failed) token exchange.  Used in URL-cleaning helpers
 * and route redirects to prevent the raw callback params from being
 * visible in the address bar or stored as redirect paths.
 */
export const OAUTH_CALLBACK_PARAMS = [
  'code',
  'state',
  'error',
  'error_description',
  'identity_provider',
] as const;
